import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Eircode entry box — IE counterpart of `UkPostcodeSearchBox`.
 *
 * Accepts any Irish Eircode (full or routing-key-only), strips the
 * unique 4-char tail, and routes to `/area-prices/{ROUTING_KEY}` —
 * the existing per-RK PPR-sold-price page that already shows median
 * sale, median ask, sale-vs-ask gap, BER mix and live listings.
 *
 * Autocomplete: hits `/api/eircode/keys/by-prefix` (mirrors the UK
 * autocomplete contract exactly).
 *
 * Routing-key validation: 1 letter + 2 alphanumerics (D02, D6W, T12,
 * H91, V94, ...). Deliberately lenient — a typo routes to a 404 page
 * with helpful copy, never blocks here.
 */
const IeEircodeSearchBox = ({ variant = 'light' }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isLight = variant === 'light';

  // Eircode normaliser → 3-char routing key (D02 XK77 → D02)
  const normalise = (raw) => {
    const cleaned = (raw || '').trim().toUpperCase().replace(/\s+/g, '');
    if (cleaned.length < 1) return null;
    if (!/^[A-Z]/.test(cleaned)) return null;
    // First 3 chars must be letter + 2 alphanumerics
    if (cleaned.length < 3) return null;
    return cleaned.slice(0, 3);
  };

  const goToRoutingKey = (rk) => {
    if (!rk) return;
    setShowDropdown(false);
    navigate(`/area-prices/${rk}`);
  };

  const fetchSuggestions = useCallback((prefix) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!prefix || prefix.length < 1) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      axios
        .get(`${BACKEND_URL}/api/eircode/keys/by-prefix`, {
          params: { prefix, limit: 6 },
        })
        .then((r) => {
          const rows = (r.data?.results || []).filter((s) => s.routing_key);
          setSuggestions(rows);
          setActiveIndex(-1);
        })
        .catch(() => setSuggestions([]));
    }, 180);
  }, []);

  const onChange = (e) => {
    const raw = e.target.value;
    setValue(raw);
    if (error) setError('');
    const cleaned = raw.trim().toUpperCase().replace(/\s+/g, '').slice(0, 3);
    fetchSuggestions(cleaned);
    setShowDropdown(true);
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (activeIndex >= 0 && suggestions[activeIndex]) {
      goToRoutingKey(suggestions[activeIndex].routing_key);
      return;
    }
    const rk = normalise(value);
    if (!rk) {
      setError('Enter a valid Eircode (e.g. D02, T12 or D04 N4X8)');
      inputRef.current?.focus();
      return;
    }
    setError('');
    goToRoutingKey(rk);
  };

  const onKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <form
      ref={wrapperRef}
      onSubmit={handleSubmit}
      data-testid="ie-eircode-search-box"
      className="relative flex items-stretch gap-2 max-w-2xl mx-auto"
    >
      <div
        className={`relative flex-1 flex items-center gap-2 rounded-full px-5 py-3 ${
          isLight
            ? 'bg-white ring-1 ring-gray-200 focus-within:ring-[#7042cc] shadow-sm'
            : 'bg-white/10 backdrop-blur ring-1 ring-white/20 focus-within:ring-white/40'
        }`}
      >
        <Sparkles className="h-4 w-4 shrink-0 text-[#7042cc]" />
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="postal-code"
          spellCheck={false}
          maxLength={8}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => value && setShowDropdown(true)}
          placeholder="Type any Eircode — e.g. D02 or T12 — see what sold near you"
          aria-label="Eircode"
          aria-autocomplete="list"
          aria-controls="ie-eircode-suggestions"
          aria-expanded={showDropdown && suggestions.length > 0}
          data-testid="ie-eircode-input"
          className={`flex-1 bg-transparent border-0 outline-none text-base tabular-nums uppercase placeholder:normal-case placeholder:text-sm ${
            isLight
              ? 'text-gray-900 placeholder:text-gray-400'
              : 'text-white placeholder:text-white/40'
          }`}
        />
      </div>
      <Button
        type="submit"
        data-testid="ie-eircode-submit"
        className="bg-[#7042cc] text-white hover:bg-[#5b35a8] rounded-full px-5 sm:px-6 font-semibold"
      >
        <span className="hidden sm:inline mr-2">See what sold</span>
        <ArrowRight className="h-4 w-4" />
      </Button>

      {showDropdown && suggestions.length > 0 && (
        <ul
          id="ie-eircode-suggestions"
          role="listbox"
          data-testid="ie-eircode-suggestions"
          className="absolute left-0 right-[140px] top-full mt-2 z-30 overflow-hidden rounded-xl bg-white text-gray-900 ring-1 ring-gray-200 shadow-lg max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, i) => {
            const active = i === activeIndex;
            return (
              <li
                key={s.routing_key}
                role="option"
                aria-selected={active}
                data-testid={`ie-eircode-suggestion-${s.routing_key}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  goToRoutingKey(s.routing_key);
                }}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 cursor-pointer text-sm ${
                  active ? 'bg-[#7042cc]/10' : 'hover:bg-gray-50'
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-gray-400" />
                  <span className="font-mono font-bold tabular-nums">{s.routing_key}</span>
                  {s.county && (
                    <span className="text-xs text-gray-500">· {s.county}</span>
                  )}
                </span>
                <span className="text-[11px] text-gray-500 tabular-nums">
                  {s.count} {s.count === 1 ? 'sale' : 'sales'}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p
          role="alert"
          data-testid="ie-eircode-error"
          className={`absolute mt-14 text-xs ${
            isLight ? 'text-red-600' : 'text-[#ffb37a]'
          }`}
        >
          {error}
        </p>
      )}
    </form>
  );
};

export default IeEircodeSearchBox;
